<template>
	<div v-if="visible" class="inputs-row" @focusout="normalizeValues">
		<template v-if="enabledComponent(0)">
			<input type="text" v-model.number="durD" class="duration-component" :class="[clazz, errorClass]" :style="style" v-bind="tabindexBinding()" :readonly="ro">
			<span class="duration-label" :class="{'duration-label-ro':ro}">d</span>
		</template>
		<template v-if="enabledComponent(1)">
			<input type="text" v-model.number="durH" class="duration-component" :class="[clazz, errorClass]" :style="style" v-bind="tabindexBinding()" :readonly="ro">
			<span class="duration-label" :class="{'duration-label-ro':ro}">h</span>
		</template>
		<template v-if="enabledComponent(2)">
			<input type="text" v-model.number="durM" class="duration-component" :class="[clazz, errorClass]" :style="style" v-bind="tabindexBinding()" :readonly="ro">
			<span class="duration-label" :class="{'duration-label-ro':ro}">m</span>
		</template>
		<template v-if="enabledComponent(3)">
			<input type="text" v-model.number="durS" class="duration-component" :class="[clazz, errorClass]" :style="style" v-bind="tabindexBinding()" :readonly="ro">
			<span class="duration-label" :class="{'duration-label-ro':ro}">s</span>
		</template>
		<template v-if="enabledComponent(4)">
			<input type="text" v-model.number="durMs" class="duration-component" :class="[clazz, errorClass]" :style="style" v-bind="tabindexBinding()" :readonly="ro">
			<span class="duration-label" :class="{'duration-label-ro':ro}">ms</span>
		</template>
	</div>
</template>

<script setup lang="ts">
import {ref, computed} from "vue";
import useEditor, {type EditorProps, editorProps} from "@fd/editors/Editor.ts";
import {injectEnv} from "@fd/Environment.ts";
import type {Input} from "@t/fd/editor/Input.ts";
import {
	ALL_DURATION_COMPONENTS,
	Duration,
	DurationComponent,
	DurationParsed,
	normalizeDuration,
	parseDuration,
	toIsoFormat, toMillis,
	ZERO
} from "@t/fd/editor/Duration.ts";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;
const env = injectEnv();

const duration = computed<Duration>(() => props.part as Duration);
const {visible, value, rawValue, localData, formData, clazz, style, ro, errorClass, tabindexBinding} = useEditor(props);

const durMs = computed<number>({
	get():number {
		return dur.value.ms;
	},
	set(v:number):void {
		const current = { ...dur.value };
		current.ms = v;
		dur.value = current;
	}
});

const durS = computed<number>({
	get():number {
		return dur.value.s;
	},
	set(v:number):void {
		const current = { ...dur.value };
		current.s = v;
		dur.value = current;
	}
});

const durM = computed<number>({
	get():number {
		return dur.value.m;
	},
	set(v:number):void {
		const current = { ...dur.value };
		current.m = v;
		dur.value = current;
	}
});

const durH = computed<number>({
	get():number {
		return dur.value.h;
	},
	set(v:number):void {
		const current = { ...dur.value };
		current.h = v;
		dur.value = current;
	}
});

const durD = computed<number>({
	get():number {
		return dur.value.d;
	},
	set(v:number):void {
		const current = { ...dur.value };
		current.d = v;
		dur.value = current;
	}
});

function normalizeValues():void {
	const current = { ...dur.value };
	dur.value = normalizeDuration(current);
}

const dur = computed<DurationParsed>({
	get():DurationParsed {
		switch (duration.value?.propertyFormat) {
			case "milliseconds": return normalizeDuration({ d:0, h:0, m:0, s:0, ms:value.value});
			case "seconds": return normalizeDuration({ d:0, h:0, m:0, s:value.value, ms:0});
			case "minutes": return normalizeDuration({ d:0, h:0, m:value.value, s:0, ms:0});
			default: return parseDuration(value.value);
		}
	},
	set(v:DurationParsed):void {
		switch (duration.value?.propertyFormat) {
			case "milliseconds":
				value.value = Math.floor(toMillis(v));
				break;
			case "seconds":
				value.value = Math.floor(toMillis(v)/1000);
				break;
			case "minutes":
				value.value = Math.floor(toMillis(v)/60000);
				break;
			default: value.value = toIsoFormat(v);
		}
	}
});

function indexOf(c:DurationComponent|undefined, defIdx:number):number {
	if (!c) return defIdx;
	for (let i = 0; i < ALL_DURATION_COMPONENTS.length; i++) {
		if (c === ALL_DURATION_COMPONENTS[i]) return i;
	}
	return defIdx;
}

const startComponentIndex = computed<number>(() => {
	return indexOf(duration.value?.highest, 0);
});

const endComponentIndex = computed<number>(() => {
	return indexOf(duration.value?.lowest, ALL_DURATION_COMPONENTS.length - 1);
});

function enabledComponent(idx:number):boolean {
	return idx >= startComponentIndex.value && idx <= endComponentIndex.value;
}

</script>

<style>
.duration-component {
	text-align: right;
	max-width: 40px;
}
.duration-label {
	opacity: 0.5;
	&:last-child {
		padding-right: var(--input-h-padding);
	}
	&.duration-label-ro {
		background-color: var(--input-disabled-bg);
		color: color-mix(in srgb, var(--input-disabled-fg), transparent);
		opacity: 1;
	}
}
</style>